import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { SignaturePad } from "../components/SignaturePad";
import { CheckCircle, Plus, Trash, Paperclip, X, Flask } from "@phosphor-icons/react";

const API_HERO_REMOVED = true; // hero image removed per owner 2026-08-27

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const DAYPARTS = ["Early morning (before 7:30 am)", "Daytime", "Evening", "Overnight"];
const EXTRA_AVAIL = ["Short shifts", "Live-in"];
const EXPERIENCE_YEARS = ["Less than 1 year", "1–2 years", "3–5 years", "More than 5 years"];
const EDUCATION = ["High school / GED", "Some college", "Associate degree", "Bachelor's degree", "Graduate degree", "Other"];

const emptyEmployer = {
  currently_employed: "", employer: "", position: "", duties: "", start_date: "", end_date: "",
  pay_rate: "", reason_leaving: "", supervisor_first: "", supervisor_last: "", supervisor_phone: "", supervisor_email: "", may_contact: "", caregiving_related: "",
};
const emptyRef = { first_name: "", last_name: "", relationship: "", years_known: "", phone: "", email: "", may_contact: "", not_relative: false };
const emptyProfRef = { name: "", company: "", phone: "", email: "", may_contact: "" };
const ACK_TEXT = {
  terms: "I certify that the information in this application is true and complete to the best of my knowledge, and I understand that false or omitted information may be grounds for not hiring me or for dismissal.",
  reference_contact: "I authorize Silver Home Care to contact the references I listed above where I answered \"yes\" to contact.",
  policy_read: "I have read and understand the scope, method, and policy stated above.",
  drug_test_accident: "I agree to submit to a drug and alcohol test in the event that I am involved in a workplace accident or incident.",
  drug_screen: "I agree to submit to a urine test or oral swab test to screen for the use of controlled substances as a condition of employment.",
  at_will: "I understand and acknowledge that employment with Silver Home Care is at-will and may be ended by either party at any time.",
};

const YesNo = ({ value, onChange, testid }) => (
  <div className="flex gap-2" data-testid={testid}>
    {["Yes", "No"].map((v) => (
      <button key={v} type="button" onClick={() => onChange(v)}
        className={`rounded-full border px-5 py-2 text-sm font-semibold transition-colors ${value === v ? "border-primary bg-primary text-white shadow-sm" : "border-stone-200 bg-white text-stone-500 hover:border-primary/40 hover:text-primary"}`}
        data-testid={`${testid}-${v.toLowerCase()}`}>
        {v}
      </button>
    ))}
  </div>
);

const F = ({ label, required, children, className = "" }) => (
  <div className={className}>
    <label className="mb-1.5 flex min-h-[1rem] items-end text-sm font-medium text-stone-600 sm:min-h-[2rem]">{label}{required && <span className="text-primary"> *</span>}</label>
    {children}
  </div>
);

const Section = ({ num, title, sub, children, testid }) => (
  <section className="rounded-2xl border border-stone-200 bg-white p-7 shadow-sm sm:p-10" data-testid={testid}>
    <div className="flex items-baseline gap-3">
      <span className="font-heading text-base font-medium text-[#6A3E97]/60">{num}</span>
      <div>
        <h2 className="font-heading text-2xl font-medium tracking-tight text-[#23282f]">{title}</h2>
        {sub && <p className="mt-1 text-sm leading-relaxed text-stone-500">{sub}</p>}
      </div>
    </div>
    <div className="mt-7">{children}</div>
  </section>
);

export default function ApplyPage() {
  const POSITION = "Direct Care Worker"; // hardcoded: this is the DCW application (most applicants come from Indeed)
  const [pitchImages, setPitchImages] = useState(["/site-care.webp"]);
  const [pitchIdx, setPitchIdx] = useState(0);
  useEffect(() => {
    api.get("/public/image-assignments").then(({ data }) => {
      const names = data?.slots?.["apply-page"];
      if (names?.length) setPitchImages(names.map((n) => `/${n}`));
    }).catch(() => {});
  }, []);
  useEffect(() => {
    if (pitchImages.length < 2) return undefined;
    const t = setInterval(() => setPitchIdx((i) => (i + 1) % pitchImages.length), 6000);
    return () => clearInterval(t);
  }, [pitchImages.length]);
  const [form, setForm] = useState({
    first_name: "", middle_name: "", last_name: "", home_phone: "", cell_phone: "", can_text: "",
    address: "", address2: "", city: "", state: "PA", zip: "", email: "",
    eligible_us: "", experience_years: "", has_license_insurance: "", reliable_vehicle: "",
    start_date: "", availability_notes: "", is_cna: "", education: "", referral: "", company: "",
    referral_code: "", cna_number: "", most_important: "", how_heard: "",
    scenario_help: "", why_notice: "", why_reliable: "", why_person: "",
  });
  const [grid, setGrid] = useState({});
  const [extras, setExtras] = useState([]);
  const [confirmed, setConfirmed] = useState(false);
  const [employers, setEmployers] = useState([{ ...emptyEmployer }]);
  const [refs, setRefs] = useState([{ ...emptyRef }, { ...emptyRef }]);
  const [profRefs, setProfRefs] = useState([]);
  const [acks, setAcks] = useState({});
  const [sig, setSig] = useState({ typed_name: "", image: null });
  const [resume, setResume] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState(false);
  const [demoFilled, setDemoFilled] = useState(false);
  const [demoUnlocked, setDemoUnlocked] = useState(() => new URLSearchParams(window.location.search).get("demo") === "1");
  const eggTaps = useRef([]);

  const eggTap = () => {
    const now = Date.now();
    eggTaps.current = [...eggTaps.current.filter((t) => now - t < 2500), now];
    if (eggTaps.current.length >= 5 && !demoUnlocked) {
      setDemoUnlocked(true);
      toast.success("Demo mode unlocked", { description: "Staff demo walkthrough is visible at the bottom of this page." });
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setEmp = (i, k) => (e) => setEmployers((l) => l.map((x, j) => (j === i ? { ...x, [k]: e.target.value } : x)));
  const setEmpVal = (i, k, v) => setEmployers((l) => l.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const setRef = (i, k) => (e) => setRefs((l) => l.map((x, j) => (j === i ? { ...x, [k]: e.target.value } : x)));
  const setRefVal = (i, k, v) => setRefs((l) => l.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const setProfRef = (i, k) => (e) => setProfRefs((l) => l.map((x, j) => (j === i ? { ...x, [k]: e.target.value } : x)));
  const setProfRefVal = (i, k, v) => setProfRefs((l) => l.map((x, j) => (j === i ? { ...x, [k]: v } : x)));

  const uploadResume = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/public/apply-upload", fd);
      setResume({ path: data.path, filename: data.filename });
      toast.success("File attached");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
    }
  };

  const makeDemoSignature = (name) => {
    const c = document.createElement("canvas");
    c.width = 900;
    c.height = 200;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.fillStyle = "#1a1a1a";
    ctx.font = "italic 600 84px 'Segoe Script', 'Brush Script MT', cursive";
    ctx.fillText(name, 60, 128);
    return c.toDataURL("image/png");
  };

  const runDemo = () => {
    const inAWeek = new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10);
    setConfirmed(true);
    setForm((f) => ({ ...f,
      first_name: "Maria", last_name: "Santos", middle_name: "",
      email: "maria.santos@demo.silverhomecare.com", home_phone: "", cell_phone: "5705550142", can_text: "Yes",
      address: "412 Maple Ave", address2: "", city: "Fairless Hills", state: "PA", zip: "19030",
      eligible_us: "Yes", experience_years: "3–5 years", has_license_insurance: "Yes", reliable_vehicle: "Yes",
      start_date: inAWeek, education: "Some college", is_cna: "Yes",
      availability_notes: "Mon–Fri from 5:30 am, short shifts welcome, every other weekend",
      referral: "", referral_code: "", cna_number: "", most_important: "I treat every client the way I would want someone to treat my own mother.",
      scenario_help: "My neighbor fell last winter and I started doing her groceries and laundry every week — nobody asked me to, she just needed it.",
      why_notice: "Small changes are the early warning — a caregiver who notices and speaks up is how a bad day gets caught before it becomes a crisis.",
      why_reliable: "Because they plan their whole day around us. When we're late they feel forgotten; when we show up on time they feel safe.",
      why_person: "The tasks are the excuse, not the point — if the towels are folded but the person feels rushed and invisible, we didn't do our job.",
      how_heard: "Saw the posting on Indeed — a friend who works with you loves it here." }));
    setGrid({ "Early morning (before 7:30 am)": ["Mon", "Tue", "Wed", "Thu", "Fri"],
              "Daytime": ["Mon", "Wed", "Fri"], "Evening": ["Sat"] });
    setExtras(["Short shifts"]);
    setEmployers([{ currently_employed: "No", employer: "Sunrise Senior Living", position: "Caregiver",
                    duties: "Personal care, meals, companionship", start_date: "2022-03-01", end_date: "2026-06-15",
                    pay_rate: "$16.00/hr", reason_leaving: "Relocated", supervisor_first: "Pam", supervisor_last: "Reyes",
                    supervisor_phone: "5705550188", supervisor_email: "pam.reyes@demo.silverhomecare.com", may_contact: "Yes" }]);
    setRefs([{ first_name: "Denise", last_name: "Cole", relationship: "Former coworker", years_known: "4", phone: "5705550177",
               email: "denise.cole@demo.silverhomecare.com", may_contact: "Yes" },
             { first_name: "Gary", last_name: "Whitaker", relationship: "Neighbor", years_known: "8", phone: "5705550191",
               email: "gary.whitaker@demo.silverhomecare.com", may_contact: "Yes" }]);
    setAcks(Object.fromEntries(Object.keys(ACK_TEXT).map((k) => [k, true])));
    setSig({ typed_name: "Maria Santos", image: makeDemoSignature("Maria Santos") });
    setDemoFilled(true);
    toast.success("Demo entries filled in — everything including the signature is done. Scroll through, then press Submit application");
    setTimeout(() => document.querySelector('[data-testid="apply-hero"]')?.scrollIntoView({ behavior: "smooth" }), 60);
  };

  const allAcks = Object.keys(ACK_TEXT).every((k) => acks[k]);
  const filledEmployers = employers.filter((e) => e.employer.trim());
  const missing = [];
  const need = (unmet, label, target) => { if (unmet) missing.push({ label, target }); };
  need(!(form.first_name.trim() && form.last_name.trim()), "Your name", "apply-section-contact");
  need(!/.+@.+\..+/.test(form.email), "A valid email", "apply-section-contact");
  need(!form.cell_phone.trim(), "Cell phone", "apply-section-contact");
  need(!form.eligible_us, "Work eligibility (yes/no)", "apply-section-contact");
  need(!form.experience_years, "Years of experience", "apply-section-contact");
  need(!form.start_date, "Start date", "apply-section-contact");
  need(!DAYPARTS.some((dp) => (grid[dp] || []).length) && !extras.length, "Availability — mark at least one box in the grid", "apply-section-availability");
  need(!filledEmployers.length, "At least one employer", "apply-section-employment");
  need(filledEmployers.some((e) => e.currently_employed !== "Yes" && !e.may_contact), "'May we contact' answer for each previous employer", "apply-section-employment");
  need(!refs.every((r) => r.first_name.trim() && r.last_name.trim() && (r.phone.trim() || r.email.trim())), "Two personal references (first & last name + phone or email)", "apply-references");
  need(!refs.every((r) => r.may_contact), "'May we contact' answer for each reference", "apply-references");
  need(!refs.every((r) => r.not_relative), "Confirm each reference is not a relative", "apply-references");
  need(!allAcks, "All acknowledgment checkboxes", "apply-acknowledgments");
  need(!sig.typed_name.trim(), "Type your full legal name", "apply-acknowledgments");
  need(!sig.image, "Draw your signature in the box", "apply-acknowledgments");
  const valid = missing.length === 0;

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/public/apply", {
        ...form,
        position: POSITION,
        name: `${form.first_name} ${form.middle_name ? form.middle_name + " " : ""}${form.last_name}`.trim(),
        phone: form.cell_phone || form.home_phone,
        availability: DAYPARTS.filter((dp) => (grid[dp] || []).length).concat(extras),
        availability_grid: grid,
        certifications: [],
        references: refs.map((r) => ({
          name: `${r.first_name.trim()} ${r.last_name.trim()}`.replace(/\s+/g, " ").trim(),
          first_name: r.first_name.trim(), last_name: r.last_name.trim(),
          relationship: `${r.relationship.trim()}${r.years_known.trim() ? ` — known ${r.years_known.trim()}` : ""}`.replace(/^ — /, ""),
          years_known: r.years_known.trim(),
          email: r.email.trim(), phone: r.phone.trim(), may_contact: r.may_contact, not_relative: !!r.not_relative,
        })),
        professional_references: profRefs.filter((r) => r.name.trim()),
        employers: filledEmployers,
        referral: new URLSearchParams(window.location.search).get("utm_source")
          ? `utm: ${new URLSearchParams(window.location.search).get("utm_source")}${new URLSearchParams(window.location.search).get("utm_campaign") ? ` / ${new URLSearchParams(window.location.search).get("utm_campaign")}` : ""}`
          : "",
        acknowledgments: acks,
        signature: { typed_name: sig.typed_name.trim(), image: sig.image, date: new Date().toISOString().slice(0, 10) },
        resume_path: resume?.path || "",
        resume_filename: resume?.filename || "",
      });
      setDone(true);
      window.scrollTo(0, 0);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setBusy(false);
  };

  const Hero = () => (
    <div className="overflow-hidden rounded-3xl border border-stone-200 bg-white shadow-sm" data-testid="apply-hero">
      <div className="border-b border-stone-200 bg-white px-6 py-9 text-center sm:flex sm:items-center sm:gap-10 sm:px-12 sm:text-left">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold uppercase tracking-[0.15em] text-[#6A3E97]">Careers at Silver Home Care</p>
          <h1 className="mx-auto mt-3 max-w-2xl font-heading text-3xl font-medium leading-[1.1] tracking-tight text-[#23282f] sm:mx-0 sm:text-4xl">Improving lives, one visit at a time.</h1>
          <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-stone-600 sm:mx-0 sm:text-lg">
            We're a non-medical home care agency serving elderly and disabled residents of Bucks and Philadelphia
            Counties. We value <span className="font-semibold text-[#6A3E97]">kindness</span>, show <span className="font-semibold text-[#6A3E97]">respect</span>,
            and act with <span className="font-semibold text-[#6A3E97]">integrity</span> — and we're looking for friendly, reliable people to join us.
          </p>
        </div>
        <img src="/apply-intro.webp" alt="A Silver Home Care caregiver serving lunch to a client in his kitchen"
          className="mx-auto mt-5 block w-36 rounded-xl shadow-md ring-1 ring-stone-200 sm:mx-0 sm:mt-0 sm:w-56 sm:shrink-0" data-testid="apply-hero-img" />
      </div>

      <div className="px-6 py-7 sm:px-12" data-testid="apply-requirements">
        <h2 className="font-heading text-2xl font-medium tracking-tight text-[#23282f]">Before you apply</h2>
        <div className="mt-5 grid gap-8 sm:grid-cols-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-stone-500">Requirements</p>
            <ul className="mt-2 list-disc space-y-1.5 pl-5 text-sm text-stone-700">
              <li>A sincere desire to serve the needs of the elderly and disabled</li>
              <li>18 years or older</li>
              <li>High school diploma or GED</li>
              <li>Reliable transportation — a dependable way to get to every shift, on time</li>
              <li>A driver's license, reliable vehicle, and valid insurance open up the most opportunities — many visits include errands and appointments, so without them the work we can offer you may be limited</li>
            </ul>
            <p className="mt-4 text-xs font-semibold uppercase tracking-wider text-stone-500">Job duties include (but may not be limited to)</p>
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid="apply-duties">
              {["Personal Care", "Medication Reminders", "Meal Preparation", "Laundry", "Housekeeping", "Appointments", "Shopping & Errands"].map((d) => (
                <span key={d} className="rounded-full bg-[#6A3E97]/10 px-3 py-1 text-xs font-medium text-[#4c2a75]">{d}</span>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-stone-500">Pre-employment screenings</p>
            <p className="mt-2 text-xs leading-relaxed text-stone-600">
              This position involves working with fragile, vulnerable or otherwise dependent individuals in their own
              homes and under minimal supervision. If you are offered a position, we will complete the required
              pre-employment screenings and clearances, including:
            </p>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-stone-700">
              <li>Drug testing</li>
              <li>Criminal records check</li>
              <li>Child abuse clearance (when required for the role)</li>
              <li>Sex offender registry check</li>
              <li>References (2)</li>
              <li>Proof of residency</li>
              <li>Medicaid exclusion list check</li>
              <li>Social Security number verification</li>
            </ul>
          </div>
        </div>
        <label className="mt-5 flex cursor-pointer items-start gap-3 rounded-xl border border-[#6A3E97]/30 bg-[#6A3E97]/5 p-4 transition-colors hover:bg-[#6A3E97]/10" data-testid="apply-requirements-confirm">
          <input type="checkbox" checked={confirmed} onChange={(e) => {
            setConfirmed(e.target.checked);
            if (e.target.checked) setTimeout(() => document.querySelector('[data-testid="apply-section-contact"]')?.scrollIntoView({ behavior: "smooth" }), 80);
          }} className="mt-0.5" />
          <span className="text-sm font-medium text-stone-800">I meet the requirements above and agree to the pre-employment screenings — take me to the application.</span>
        </label>
        <p className="mt-4 text-[11px] leading-relaxed text-stone-400">
          Silver Home Care is an equal opportunity employer. This application will not be used for limiting or excluding any
          applicant from consideration for employment on a basis prohibited by local, state or federal law. If you need a
          reasonable accommodation during the application process, please call us at 800-719-6912.
        </p>
      </div>
    </div>
  );

  if (done) {
    return (
      <div className="min-h-screen bg-[#F5F2FA]">
        <div className="mx-auto max-w-7xl px-6 py-10">
          <Hero />
          <div className="mt-6 rounded-2xl border border-stone-200 bg-white p-10 text-center shadow-sm" data-testid="apply-success">
            <CheckCircle size={48} weight="fill" className="mx-auto text-primary" />
            <h2 className="mt-4 font-heading text-2xl font-bold text-stone-900">Application received</h2>
            <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-stone-600">
              Thank you for applying to Silver Home Care. We review every application carefully — if your experience
              and availability match an open position, we will follow up with you about next steps.
            </p>
            <p className="mt-3 text-xs text-stone-400">A confirmation email is on its way to you.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F2FA]" data-testid="apply-page">
      <header className="sticky top-0 z-40 border-b border-stone-200 bg-white" data-testid="apply-nav">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6">
          <a href="/"><img src="/shc-logo.png" alt="Silver Home Care" className="h-11 w-auto" /></a>
          <nav className="hidden items-center gap-6 text-sm font-medium text-stone-600 md:flex" data-testid="page-nav">
            <a href="/#services" className="transition-colors hover:text-[#6A3E97]">Services</a>
            <a href="/#about" className="transition-colors hover:text-[#6A3E97]">About us</a>
            <a href="/#contact" className="transition-colors hover:text-[#6A3E97]">Contact us</a>
          </nav>
          <div className="flex items-center gap-4">
            <a href="/" className="text-sm font-medium text-stone-600 transition-colors hover:text-[#6A3E97] md:hidden" data-testid="apply-back-site">← Back to site</a>
            <a href="tel:8007196912" className="inline-flex items-center gap-1.5 rounded-md bg-[#6A3E97] px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-[#55317A]" data-testid="apply-call-cta">800-719-6912</a>
          </div>
        </div>
      </header>
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-14">
        <Hero />

        <div className="mt-10 grid items-center gap-10 overflow-hidden rounded-3xl border border-stone-200 bg-white p-7 shadow-sm sm:p-10 lg:grid-cols-2" data-testid="apply-pitch">
          <div className="relative" data-testid="apply-pitch-carousel">
            {pitchImages.map((src, i) => (
              <img key={src} src={src} alt="A Silver Home Care caregiver with a client at home"
                data-testid={i === pitchIdx ? "apply-pitch-img" : undefined}
                className={`w-full rounded-xl object-cover transition-opacity duration-700 ${i === pitchIdx ? "relative opacity-100" : "absolute inset-0 h-full opacity-0"}`} />
            ))}
          </div>
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.15em] text-[#6A3E97]">Careers — come work for us</p>
            <h2 className="mt-3 font-heading text-3xl font-medium leading-tight tracking-tight text-[#23282f] sm:text-4xl">Caregivers who pay attention — that's who we hire.</h2>
            <p className="mt-4 max-w-lg text-lg leading-relaxed text-stone-600">
              If that's you, we'd like to meet you. You'll work for local, hands-on owners who know your name
              and answer when you call — work that matters, every visit. The application takes about ten minutes.
            </p>
          </div>
        </div>

        <div className="mt-10 space-y-8">
          {confirmed && (<>
          <Section num="01" title="About you" sub="How we reach you, and the basics." testid="apply-section-contact">
            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-3">
                <F label="First name" required><Input value={form.first_name} onChange={set("first_name")} className="border-stone-300 bg-white" data-testid="apply-first-name" /></F>
                <F label="Middle name"><Input value={form.middle_name} onChange={set("middle_name")} className="border-stone-300 bg-white" data-testid="apply-middle-name" /></F>
                <F label="Last name" required><Input value={form.last_name} onChange={set("last_name")} className="border-stone-300 bg-white" data-testid="apply-last-name" /></F>
              </div>
              <div className="grid gap-4 sm:grid-cols-3">
                <F label="Home phone"><Input value={form.home_phone} onChange={set("home_phone")} placeholder="(000) 000-0000" className="border-stone-300 bg-white" data-testid="apply-home-phone" /></F>
                <F label="Cell phone" required><Input value={form.cell_phone} onChange={set("cell_phone")} placeholder="(000) 000-0000" className="border-stone-300 bg-white" data-testid="apply-cell-phone" /></F>
                <F label="Can we text you?"><YesNo value={form.can_text} onChange={(v) => setForm((f) => ({ ...f, can_text: v }))} testid="apply-can-text" /></F>
              </div>
              <p className="rounded-lg border border-[#4A235A]/20 bg-[#4A235A]/[0.04] px-3 py-2 text-xs text-stone-600" data-testid="apply-954-note">
                <b>Expect our call from a 954 area code.</b> When we reach out about your application, it will come from a 954 number (our staffing line) — answer or call us back, since unknown numbers are easy to miss.
              </p>
              <div className="grid gap-4 sm:grid-cols-2">
                <F label="Street address"><Input value={form.address} onChange={set("address")} className="border-stone-300 bg-white" data-testid="apply-address" /></F>
                <F label="Address line 2"><Input value={form.address2} onChange={set("address2")} className="border-stone-300 bg-white" data-testid="apply-address2" /></F>
              </div>
              <div className="grid gap-4 sm:grid-cols-3">
                <F label="City"><Input value={form.city} onChange={set("city")} className="border-stone-300 bg-white" data-testid="apply-city" /></F>
                <F label="State"><Input value={form.state} onChange={set("state")} className="border-stone-300 bg-white" data-testid="apply-state" /></F>
                <F label="Zip"><Input value={form.zip} onChange={set("zip")} className="border-stone-300 bg-white" data-testid="apply-zip" /></F>
              </div>
              <F label="Email" required><Input type="email" value={form.email} onChange={set("email")} placeholder="example@example.com" className="border-stone-300 bg-white" data-testid="apply-email" /></F>

              <div className="grid gap-4 border-t border-stone-100 pt-4 sm:grid-cols-2">
                <F label="Legally eligible to work in the US?" required><YesNo value={form.eligible_us} onChange={(v) => setForm((f) => ({ ...f, eligible_us: v }))} testid="apply-eligible" /></F>
                <F label="Years of caregiving experience" required>
                  <select value={form.experience_years} onChange={set("experience_years")} className="h-9 w-full rounded-md border border-stone-300 bg-white px-3 text-sm" data-testid="apply-experience-years">
                    <option value="">—</option>
                    {EXPERIENCE_YEARS.map((o) => <option key={o}>{o}</option>)}
                  </select>
                </F>
                <F label="Valid driver's license & vehicle insurance?"><YesNo value={form.has_license_insurance} onChange={(v) => setForm((f) => ({ ...f, has_license_insurance: v }))} testid="apply-license" /></F>
                <F label="Reliable vehicle?"><YesNo value={form.reliable_vehicle} onChange={(v) => setForm((f) => ({ ...f, reliable_vehicle: v }))} testid="apply-vehicle" /></F>
                <F label="When can you start?" required><Input type="date" value={form.start_date} onChange={set("start_date")} className="border-stone-300 bg-white" data-testid="apply-start-date" /></F>
                <F label="Highest level of education">
                  <select value={form.education} onChange={set("education")} className="h-9 w-full rounded-md border border-stone-300 bg-white px-3 text-sm" data-testid="apply-education">
                    <option value="">—</option>
                    {EDUCATION.map((o) => <option key={o}>{o}</option>)}
                  </select>
                </F>
              </div>

            </div>
          </Section>

          <Section num="02" title="Your availability" sub="Very important — we match caregivers to open positions by availability, so be as accurate as you can." testid="apply-section-availability">
            <div className="space-y-4">
              <div>
                <p className="mb-2 text-xs font-medium text-stone-500">Mark every day and time you can work: <span className="text-primary">*</span></p>
                <div className="overflow-x-auto rounded-xl border border-stone-200" data-testid="apply-availability-grid">
                  <table className="w-full min-w-[560px] text-xs">
                    <thead>
                      <tr className="bg-stone-50 text-stone-400">
                        <th className="px-3 py-2 text-left font-medium"></th>
                        {DAYS.map((d) => <th key={d} className="px-1 py-2 text-center font-medium">{d}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {DAYPARTS.map((dp) => (
                        <tr key={dp} className="border-t border-stone-100">
                          <td className="px-3 py-1.5 text-left text-xs font-medium text-stone-600">{dp}</td>
                          {DAYS.map((d) => {
                            const on = (grid[dp] || []).includes(d);
                            return (
                              <td key={d} className="px-1 py-1.5 text-center">
                                <button type="button" onClick={() => setGrid((g) => ({ ...g, [dp]: on ? (g[dp] || []).filter((x) => x !== d) : [...(g[dp] || []), d] }))}
                                  className={`h-7 w-7 rounded-md border transition-colors ${on ? "border-primary bg-primary" : "border-stone-200 bg-white hover:border-primary/50"}`}
                                  aria-label={`${dp} ${d}`} data-testid={`apply-grid-${dp.toLowerCase().replace(/\W+/g, "-")}-${d.toLowerCase()}`}>
                                  {on && <span className="text-xs font-bold text-white">✓</span>}
                                </button>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div>
                <p className="mb-2 text-xs font-medium text-stone-500">Also:</p>
                <div className="flex flex-wrap gap-2">
                  {EXTRA_AVAIL.map((a) => (
                    <button key={a} type="button"
                      onClick={() => setExtras((l) => (l.includes(a) ? l.filter((x) => x !== a) : [...l, a]))}
                      className={`rounded-full border px-4 py-2 text-xs font-semibold transition-colors ${extras.includes(a) ? "border-primary bg-primary text-white shadow-sm" : "border-stone-200 bg-white text-stone-500 hover:border-primary/40 hover:text-primary"}`}
                      data-testid={`apply-avail-${a.toLowerCase().replace(/\s+/g, "-")}`}>
                      {a}
                    </button>
                  ))}
                </div>
              </div>
              <F label="Anything else about your availability?">
                <Input value={form.availability_notes} onChange={set("availability_notes")} placeholder="Optional — e.g. available every other weekend, no overnights on school nights" className="border-stone-300 bg-white" data-testid="apply-availability-notes" />
              </F>
            </div>
          </Section>

          <Section num="03" title="Employment history" sub="Start with your current or most recent employer. With your permission, we verify your most recent position — please add a supervisor's name and email or phone. If we can't reach them, we'll try your most recent caregiving-related job." testid="apply-section-employment">
            {employers.map((emp, i) => (
              <div key={i} className="mt-4 space-y-4 rounded-xl border border-stone-100 bg-accent/30 p-4 first:mt-0" data-testid={`apply-employer-${i}`}>
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">Employer {i + 1}</p>
                  {i > 0 && (
                    <button type="button" onClick={() => setEmployers((l) => l.filter((_, j) => j !== i))} className="text-stone-400 hover:text-[#C0392B]" data-testid={`apply-employer-remove-${i}`}><Trash size={15} /></button>
                  )}
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <F label="Employer" required={i === 0}><Input value={emp.employer} onChange={setEmp(i, "employer")} className="border-stone-300 bg-white" data-testid={`apply-emp${i}-employer`} /></F>
                  <F label="Your position"><Input value={emp.position} onChange={setEmp(i, "position")} className="border-stone-300 bg-white" data-testid={`apply-emp${i}-position`} /></F>
                </div>
                <F label="Current duties"><Input value={emp.duties} onChange={setEmp(i, "duties")} className="border-stone-300 bg-white" data-testid={`apply-emp${i}-duties`} /></F>
                <div className="grid gap-4 sm:grid-cols-3">
                  <F label="Currently employed here?"><YesNo value={emp.currently_employed} onChange={(v) => setEmpVal(i, "currently_employed", v)} testid={`apply-emp${i}-current`} /></F>
                  <F label="Start date"><Input type="date" value={emp.start_date} onChange={setEmp(i, "start_date")} className="border-stone-300 bg-white" data-testid={`apply-emp${i}-start`} /></F>
                  <F label="End date (blank if current)"><Input type="date" value={emp.end_date} onChange={setEmp(i, "end_date")} disabled={emp.currently_employed === "Yes"} className="border-stone-300 bg-white disabled:opacity-50" data-testid={`apply-emp${i}-end`} /></F>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <F label="Pay rate"><Input value={emp.pay_rate} onChange={setEmp(i, "pay_rate")} placeholder="e.g. $15.50/hr" className="border-stone-300 bg-white" data-testid={`apply-emp${i}-pay`} /></F>
                  {emp.currently_employed !== "Yes" && (
                    <F label="Reason for leaving"><Input value={emp.reason_leaving} onChange={setEmp(i, "reason_leaving")} className="border-stone-300 bg-white" data-testid={`apply-emp${i}-reason`} /></F>
                  )}
                </div>
                <div className="grid gap-4 border-t border-stone-200/70 pt-3 sm:grid-cols-2">
                  <F label="Supervisor first name"><Input value={emp.supervisor_first} onChange={setEmp(i, "supervisor_first")} className="border-stone-300 bg-white" data-testid={`apply-emp${i}-sup-first`} /></F>
                  <F label="Supervisor last name"><Input value={emp.supervisor_last} onChange={setEmp(i, "supervisor_last")} className="border-stone-300 bg-white" data-testid={`apply-emp${i}-sup-last`} /></F>
                  {emp.currently_employed !== "Yes" && (<>
                    <F label="Supervisor phone"><Input value={emp.supervisor_phone} onChange={setEmp(i, "supervisor_phone")} placeholder="(000) 000-0000" className="border-stone-300 bg-white" data-testid={`apply-emp${i}-sup-phone`} /></F>
                    <F label="Supervisor email"><Input type="email" value={emp.supervisor_email} onChange={setEmp(i, "supervisor_email")} className="border-stone-300 bg-white" data-testid={`apply-emp${i}-sup-email`} /></F>
                  </>)}
                </div>
                {emp.currently_employed === "Yes" ? (
                  <p className="rounded-lg bg-[#27AE60]/5 px-3 py-2 text-[11px] font-medium text-[#1e8449]" data-testid={`apply-emp${i}-never-contact`}>
                    We never contact a current employer — no supervisor email or phone needed for this one.
                  </p>
                ) : (
                  <F label="May we contact this previous employer?" required><YesNo value={emp.may_contact} onChange={(v) => setEmpVal(i, "may_contact", v)} testid={`apply-emp${i}-contact`} /></F>
                )}
                <F label="Was this job caregiving-related?"><YesNo value={emp.caregiving_related} onChange={(v) => setEmpVal(i, "caregiving_related", v)} testid={`apply-emp${i}-caregiving`} /></F>
                <p className="text-[11px] text-stone-400">We only contact previous employers you answer "yes" for — and never your current employer.</p>
              </div>
            ))}
            <button type="button" onClick={() => setEmployers((l) => [...l, { ...emptyEmployer }])}
              className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-stone-300 px-4 py-2 text-xs font-medium text-stone-600 hover:bg-stone-900 hover:text-white"
              data-testid="apply-add-employer">
              <Plus size={14} /> Add another employer
            </button>
          </Section>

          <Section num="04" title="Two personal references" sub="Not related to you — we ask them to attest to that. Each reference you list gets a short email (and soon a text) with six quick questions — how they know you, whether they know you to be kind and of good character, and whether they'd recommend you to care for someone in their own home. Please give them a heads-up so it doesn't land as a surprise. Two completed references are required to move forward." testid="apply-references">
            {refs.map((r, i) => (
              <div key={i} className="mt-4 space-y-3 rounded-xl border border-stone-100 bg-accent/30 p-4 first:mt-0" data-testid={`apply-ref-${i}`}>
                <p className="text-xs font-semibold uppercase tracking-wide text-stone-500" data-testid={`apply-ref-label-${i}`}>
                  Reference {i + 1}{(r.first_name.trim() || r.last_name.trim()) ? ` — ${[r.first_name.trim(), r.last_name.trim()].filter(Boolean).join(" ")}` : ""}
                </p>
                <div className="grid gap-3 sm:grid-cols-2">
                  <F label="First name" required><Input value={r.first_name} onChange={setRef(i, "first_name")} className="border-stone-300 bg-white" data-testid={`apply-ref${i}-first-name`} /></F>
                  <F label="Last name" required><Input value={r.last_name} onChange={setRef(i, "last_name")} className="border-stone-300 bg-white" data-testid={`apply-ref${i}-last-name`} /></F>
                  <F label="How do they know you?" required><Input value={r.relationship} onChange={setRef(i, "relationship")} placeholder="e.g. former coworker, neighbor" className="border-stone-300 bg-white" data-testid={`apply-ref${i}-relationship`} /></F>
                  <F label="Years they've known you" required><Input value={r.years_known} onChange={setRef(i, "years_known")} placeholder="e.g. 5" className="border-stone-300 bg-white" data-testid={`apply-ref${i}-years`} /></F>
                  <F label="Phone (phone or email required)"><Input value={r.phone} onChange={setRef(i, "phone")} placeholder="(000) 000-0000" className="border-stone-300 bg-white" data-testid={`apply-ref${i}-phone`} /></F>
                  <F label="Email (phone or email required)"><Input type="email" value={r.email} onChange={setRef(i, "email")} className="border-stone-300 bg-white" data-testid={`apply-ref${i}-email`} /></F>
                  <F label="May we contact this reference?" required><YesNo value={r.may_contact} onChange={(v) => setRefVal(i, "may_contact", v)} testid={`apply-ref${i}-contact`} /></F>
                </div>
                <label className="flex cursor-pointer items-center gap-2 text-sm text-stone-700" data-testid={`apply-ref${i}-not-relative-label`}>
                  <input type="checkbox" checked={!!r.not_relative} onChange={(e) => setRefVal(i, "not_relative", e.target.checked)} data-testid={`apply-ref${i}-not-relative`} />
                  <span>This reference is <span className="font-semibold">not related to me</span> and is not a family member. <span className="text-primary">*</span></span>
                </label>
              </div>
            ))}
          </Section>

          <Section num="05" title="Professional references" sub="Optional — coworkers, supervisors, or anyone who can speak to your work." testid="apply-prof-references">
            {profRefs.map((r, i) => (
              <div key={i} className="mt-4 space-y-3 rounded-xl border border-stone-100 bg-accent/30 p-4 first:mt-0" data-testid={`apply-profref-${i}`}>
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">Professional reference {i + 1}</p>
                  <button type="button" onClick={() => setProfRefs((l) => l.filter((_, j) => j !== i))} className="text-stone-400 hover:text-[#C0392B]" data-testid={`apply-profref-remove-${i}`}><Trash size={15} /></button>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <F label="Full name"><Input value={r.name} onChange={setProfRef(i, "name")} className="border-stone-300 bg-white" data-testid={`apply-profref${i}-name`} /></F>
                  <F label="Company"><Input value={r.company} onChange={setProfRef(i, "company")} className="border-stone-300 bg-white" data-testid={`apply-profref${i}-company`} /></F>
                  <F label="Phone"><Input value={r.phone} onChange={setProfRef(i, "phone")} className="border-stone-300 bg-white" data-testid={`apply-profref${i}-phone`} /></F>
                  <F label="Email"><Input type="email" value={r.email} onChange={setProfRef(i, "email")} className="border-stone-300 bg-white" data-testid={`apply-profref${i}-email`} /></F>
                </div>
                <F label="May we contact them?"><YesNo value={r.may_contact} onChange={(v) => setProfRefVal(i, "may_contact", v)} testid={`apply-profref${i}-contact`} /></F>
              </div>
            ))}
            {profRefs.length < 2 && (
              <button type="button" onClick={() => setProfRefs((l) => [...l, { ...emptyProfRef }])}
                className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-stone-300 px-4 py-2 text-xs font-medium text-stone-600 hover:bg-stone-900 hover:text-white"
                data-testid="apply-add-profref">
                <Plus size={14} /> Add a professional reference
              </button>
            )}
            {profRefs.length === 0 && <p className="mt-2 text-xs text-stone-400">No professional references added — that's fine.</p>}
          </Section>

          <Section num="06" title="About you" sub="The part no form can capture." testid="apply-about">
            <div className="space-y-4">
              <F label="What do you feel is the most important thing we should know about you?">
                <Textarea rows={3} value={form.most_important} onChange={set("most_important")} className="border-stone-300 bg-white" data-testid="apply-most-important" />
              </F>
              <F label="Tell us how you heard about us and why you are interested in working for Silver Home Care">
                <Textarea rows={3} value={form.how_heard} onChange={set("how_heard")} className="border-stone-300 bg-white" data-testid="apply-how-heard" />
              </F>
              <p className="text-xs font-medium text-stone-500">A few honest questions — no perfect answers. We just want to know how you think about caring for someone.</p>
              <F label="Tell us about a time you helped someone when you didn't have to.">
                <Textarea rows={2} value={form.scenario_help} onChange={set("scenario_help")} className="border-stone-300 bg-white" data-testid="apply-scenario-help" />
              </F>
              <F label="If you noticed a small change in someone you were helping — their mood, appetite, or how they were getting around — why do you think that would matter?">
                <Textarea rows={2} value={form.why_notice} onChange={set("why_notice")} className="border-stone-300 bg-white" data-testid="apply-why-notice" />
              </F>
              <F label="This work happens in people's homes, on their routines. Why do you think showing up when you say you will matters so much?">
                <Textarea rows={2} value={form.why_reliable} onChange={set("why_reliable")} className="border-stone-300 bg-white" data-testid="apply-why-reliable" />
              </F>
              <F label="What do you think is the difference between doing tasks for someone and actually caring for them?">
                <Textarea rows={2} value={form.why_person} onChange={set("why_person")} className="border-stone-300 bg-white" data-testid="apply-why-person" />
              </F>
              <F label="Referral code (if you have one)"><Input value={form.referral_code} onChange={set("referral_code")} className="border-stone-300 bg-white sm:max-w-xs" data-testid="apply-referral-code" /></F>
              <div>
                <p className="mb-1 block text-xs font-medium text-stone-500">Cover letter, resume, or written references (optional)</p>
                {resume ? (
                  <div className="flex items-center gap-2 rounded-lg border border-stone-200 bg-accent/30 px-3 py-2 text-sm" data-testid="apply-resume-attached">
                    <Paperclip size={14} className="text-primary" />
                    <span className="flex-1 truncate text-stone-700">{resume.filename}</span>
                    <button type="button" onClick={() => setResume(null)} className="text-stone-400 hover:text-[#C0392B]" data-testid="apply-resume-remove"><X size={14} /></button>
                  </div>
                ) : (
                  <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-stone-300 px-4 py-2 text-xs font-medium text-stone-600 hover:bg-stone-900 hover:text-white" data-testid="apply-resume-upload">
                    <Paperclip size={14} /> {uploading ? "Uploading…" : "Attach a file"}
                    <input type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp" className="hidden" onChange={(e) => uploadResume(e.target.files?.[0])} />
                  </label>
                )}
              </div>
            </div>
          </Section>

          <Section num="07" title="Acknowledgments & signature" sub="Please read each one." testid="apply-acknowledgments">
            <div className="space-y-3">
              {Object.entries(ACK_TEXT).map(([k, text]) => (
                <label key={k} className="flex cursor-pointer items-start gap-3 rounded-lg border border-stone-100 bg-accent/20 p-3 transition-colors hover:bg-accent/50" data-testid={`apply-ack-${k.replace(/_/g, "-")}`}>
                  <input type="checkbox" checked={!!acks[k]} onChange={(e) => setAcks((a) => ({ ...a, [k]: e.target.checked }))} className="mt-0.5" />
                  <span className="text-sm leading-relaxed text-stone-700">{text}</span>
                </label>
              ))}
              <div className="pt-2">
                <F label="Type your full legal name to confirm your signature" required>
                  <Input value={sig.typed_name} onChange={(e) => setSig((s) => ({ ...s, typed_name: e.target.value }))} className="border-stone-300 bg-white" data-testid="apply-signature-name" />
                </F>
                <div className="mt-3">
                  <p className="mb-1 block text-xs font-medium text-stone-500">Please sign legibly <span className="text-primary">*</span></p>
                  <SignaturePad onChange={(img) => setSig((s) => ({ ...s, image: img }))} />
                </div>
                <p className="mt-2 text-xs text-stone-400">Date: {new Date().toLocaleDateString()}</p>
              </div>
            </div>
          </Section>

          <div className="rounded-2xl border border-stone-200 bg-white p-6 shadow-sm sm:p-8">
            <input type="text" value={form.company} onChange={set("company")} tabIndex={-1} autoComplete="off"
              className="pointer-events-none absolute -left-[9999px] h-0 w-0 opacity-0" aria-hidden="true" />
            <Button onClick={submit} disabled={busy || uploading || !valid}
              className="h-12 w-full rounded-full bg-primary text-base font-semibold text-primary-foreground shadow-sm transition-all hover:bg-stone-900 active:scale-[0.99]" data-testid="apply-submit">
              {busy ? "Submitting…" : "Submit application"}
            </Button>
            {!valid && (
              <div className="mt-4" data-testid="apply-valid-hint">
                <p className="text-center text-xs font-semibold text-stone-600">
                  Still needed before you can submit — tap one to jump straight to it:
                </p>
                <div className="mt-2 flex flex-wrap justify-center gap-1.5">
                  {missing.map((m, i) => (
                    <button key={`${m.target}-${i}`} type="button"
                      onClick={() => document.querySelector(`[data-testid="${m.target}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" })}
                      className="rounded-full border border-[#C0392B]/30 bg-[#C0392B]/5 px-3 py-1.5 text-[11px] font-medium text-[#C0392B] transition-colors hover:bg-[#C0392B] hover:text-white"
                      data-testid={`apply-missing-${i}`}>
                      {m.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <p className="mt-2 text-center text-[11px] text-stone-400 select-none" onClick={eggTap} data-testid="apply-easter-egg-trigger">You'll receive a confirmation email right away.</p>
          </div>
          </>)}

          {demoUnlocked && (
          <div className="rounded-2xl border border-dashed border-stone-300 bg-white/70 p-6 text-center" data-testid="apply-demo-card">
            <p className="text-xs font-semibold uppercase tracking-widest text-stone-400">Staff demo — temporary</p>
            <p className="mx-auto mt-2 max-w-sm text-xs leading-relaxed text-stone-500">
              Fills this application with demo entries (Maria Santos, demo contact info) so you can walk through it exactly
              like an applicant. Submit it, and every pipeline email — confirmation, prescreen, interview, references, offer —
              comes to Kelly.
            </p>
            <button type="button" onClick={runDemo}
              className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-[#6A3E97]/40 px-5 py-2 text-xs font-semibold text-[#6A3E97] transition-colors hover:bg-[#6A3E97] hover:text-white"
              data-testid="apply-demo-button">
              <Flask size={14} /> {demoFilled ? "Demo filled — scroll up and review" : "Walk through a demo application"}
            </button>
            {demoFilled && (
              <p className="mx-auto mt-3 max-w-sm rounded-lg bg-[#6A3E97]/5 p-3 text-xs leading-relaxed text-[#4c2a75]" data-testid="apply-demo-done">
                Everything is filled in — scroll through each section, change anything you like, then press
                <span className="font-semibold"> Submit application</span>. You'll get the applicant confirmation and the
                prescreen email, then continue in <span className="font-semibold">Hiring</span>.
              </p>
            )}
          </div>
          )}
        </div>
      </div>
    </div>
  );
}
